// Implements: F-SHR-006, F-UIX-007
/**
 * Mockup v3 P8: a link's label, renamed in place (the mockup's `lblrename`): the pencil turns the
 * label into a field, Enter or "Guardar" sends it, Escape or "Cancelar" puts the old label back
 * without a request. Presentation with its own editing state; the write is the caller's
 * (`onRename`, which answers the refusal's sentence or `null`).
 *
 * The label stays what the server lists: a refused rename (a `409` on a link revoked meanwhile)
 * shows the server's sentence and goes back to the old label, and an accepted one is shown once the
 * list is read again. Escape is claimed here (`preventDefault`), so it cancels the field and never
 * closes the popover or dialog around it. Leaving the field for anything but its own "Guardar" or
 * "Cancelar" cancels the edit too. The focus goes back to the pencil when the field closes.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import styles from "./LinkLabel.module.css";

export interface LinkLabelProps {
  /** The label as the server lists it (`""` for none). */
  readonly label: string;
  /** What the row is read by: the label, or "Sin etiqueta". */
  readonly name: string;
  /** Whether the pencil is offered (never on a revoked link, never without `may_revoke`). */
  readonly editable: boolean;
  /** Sends the new label; answers the sentence to show when it is refused, `null` when accepted. */
  readonly onRename?: ((label: string) => Promise<string | null>) | undefined;
}

export function LinkLabel({ label, name, editable, onRename }: LinkLabelProps): ReactElement {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const pencil = useRef<HTMLButtonElement>(null);
  const editor = useRef<HTMLSpanElement>(null);
  const focusPencil = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (editing) field.current?.focus();
    else if (focusPencil.current) {
      focusPencil.current = false;
      pencil.current?.focus();
    }
  }, [editing]);

  const close = (): void => {
    focusPencil.current = true;
    setEditing(false);
  };

  const save = async (): Promise<void> => {
    if (pending || onRename === undefined) return;
    if (draft === label) {
      close();
      return;
    }
    setPending(true);
    const refused = await onRename(draft);
    if (!mounted.current) return;
    setPending(false);
    setRefusal(refused);
    close();
  };

  if (!editing) {
    return (
      <span className={styles.show}>
        <span>{name}</span>
        {editable && onRename !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            buttonRef={pencil}
            label={t("sharing.label.rename", { label: name })}
            onClick={() => {
              setDraft(label);
              setRefusal(null);
              setEditing(true);
            }}
          >
            <Icon name="tag" size="sm" />
          </Button>
        ) : null}
        {refusal === null ? null : (
          <span role="alert" className={styles.refusal}>
            {refusal}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      ref={editor}
      className={styles.edit}
      onBlur={(event) => {
        // Focus moving to Guardar or Cancelar stays inside the editor; anywhere else cancels.
        if (pending || editor.current?.contains(event.relatedTarget) === true) return;
        setEditing(false);
      }}
    >
      <input
        ref={field}
        className={styles.input}
        aria-label={t("sharing.label.input")}
        value={draft}
        disabled={pending}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void save();
          } else if (event.key === "Escape") {
            // Claimed here: the popover or dialog around the row stays open (`escapeLayers`).
            event.preventDefault();
            close();
          }
        }}
      />
      <Button variant="primary" size="sm" loading={pending} disabled={pending} onClick={() => void save()}>
        {t("sharing.label.save")}
      </Button>
      <Button variant="ghost" size="sm" disabled={pending} onClick={close}>
        {t("sharing.label.cancel")}
      </Button>
    </span>
  );
}
